import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Paper, Title, Text, Stack, Button, Group, Loader, Center, Divider, Box, List } from '@mantine/core';
import { IconArrowLeft, IconBookmark, IconTarget, IconList, IconTool } from '@tabler/icons-react';
import { StoredCourse, courseService } from '../services/courseService';
import CourseContentDisplay from './CourseContentDisplay';
import { CourseState, CourseContent } from '../types';
import { useAuth } from '../../auth/context/AuthContext';

interface CourseViewProps {
  course?: StoredCourse;
}

// Helper function to extract course summary from content
const extractCourseSummary = (content: CourseContent) => {
  // Extract learning objectives
  const extractLearningObjectives = (content: string): string[] => {
    try {
      const objectivesSection = content.split('Key Learning Objectives:')[1]?.split('\n\n')[0] || '';
      return objectivesSection
        .split('\n')
        .map(line => line.trim())
        .filter(line => line && !line.includes(':'));
    } catch (error) {
      console.warn('Error extracting learning objectives:', error);
      return [];
    }
  };

  // Extract core concepts
  const extractCoreConcepts = (content: string): string[] => {
    try {
      const conceptsSection = content.split('Core Concepts:')[1]?.split('\n\n')[0] || '';
      return conceptsSection
        .split('\n')
        .map(line => line.trim())
        .filter(line => line && !line.includes(':'));
    } catch (error) {
      console.warn('Error extracting core concepts:', error);
      return [];
    }
  };

  // Extract short assignment summary
  const extractAssignmentSummary = (assignment: string): string => {
    try {
      // Limit to first 150 characters
      return assignment.length > 150 
        ? `${assignment.substring(0, 150)}...` 
        : assignment;
    } catch (error) {
      console.warn('Error extracting assignment summary:', error);
      return '';
    }
  };

  return {
    learningObjectives: extractLearningObjectives(content.content || ''),
    coreConcepts: extractCoreConcepts(content.content || ''),
    assignmentSummary: extractAssignmentSummary(content.assignment || '')
  };
};

const CourseView: React.FC<CourseViewProps> = ({ course: initialCourse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [course, setCourse] = useState<StoredCourse | undefined>(
    initialCourse || (location.state as { course: StoredCourse })?.course
  );
  const [loading, setLoading] = useState(!course);

  // Add a useEffect to handle location state changes immediately
  useEffect(() => {
    // Update course when location state changes
    const locationCourse = (location.state as { course: StoredCourse })?.course;
    if (locationCourse) {
      setCourse(locationCourse);
    }
  }, [location.state]);

  // Update course when initialCourse changes
  useEffect(() => {
    if (initialCourse) {
      setCourse(initialCourse);
    }
  }, [initialCourse]);

  useEffect(() => {
    const loadFirstCourse = async () => {
      // Only load if no course is provided and user is authenticated
      if (!course && user) {
        try {
          setLoading(true);
          const userCourses = await courseService.getUserCourses(user.uid);
          
          if (userCourses.length > 0) {
            // Sort courses using the same logic as the navbar
            const sortedCourses = userCourses.sort((a, b) => {
              // Sort by state priority: in_progress -> not_started -> completed
              const stateOrder = { 'in_progress': 0, 'not_started': 1, 'completed': 2 };
              const stateCompare = stateOrder[a.state] - stateOrder[b.state];
              if (stateCompare !== 0) return stateCompare;
              
              // If same state, sort by creation date (newest first)
              return b.createdAt.getTime() - a.createdAt.getTime();
            });

            setCourse(sortedCourses[0]);
          }
        } catch (error) {
          console.error('Error loading first course:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadFirstCourse();
  }, [course, user]);

  if (loading) {
    return (
      <Center style={{ height: '100vh' }}>
        <Loader size="xl" variant="dots" />
      </Center>
    );
  }

  if (!course) {
    return (
      <Paper p="md">
        <Text>No courses found. Create your first course to get started!</Text>
      </Paper>
    );
  }

  const handleStateChange = (newState: CourseState) => {
    setCourse(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        state: newState,
        content: {
          ...prev.content,
          state: newState
        }
      };
    });
  };

  // Extract summary information for "not_started" courses
  const showSummary = course.state === 'not_started';
  const summary = showSummary ? extractCourseSummary(course.content) : null;

  return (
    <Paper p="md" radius="md" withBorder>
      <Stack spacing="xl">
        <Group position="apart" align="center">
          <Button 
            variant="subtle" 
            leftIcon={<IconArrowLeft size={16} />}
            onClick={() => navigate('/')}
          >
            Back to Generator
          </Button>
          <Text size="sm" color="dimmed">
            Created on {new Date(course.createdAt).toLocaleDateString()}
          </Text>
        </Group>

        <Title order={2}>{course.input.topicName}</Title>

        {course.input.additionalContext && (
          <Paper p="md" withBorder>
            <Stack spacing="xs">
              <Text weight={500}>Additional Context:</Text>
              <Text>{course.input.additionalContext}</Text>
            </Stack>
          </Paper>
        )}
        
        {showSummary && summary && (
          <Paper p="xl" withBorder shadow="sm" radius="md">
            <Stack spacing="lg">
              <Title order={3}>Module Summary</Title>
              
              {summary.learningObjectives.length > 0 && (
                <Box>
                  <Group spacing="xs" mb="xs">
                    <IconTarget size={20} />
                    <Text weight={600}>Learning Objectives</Text>
                  </Group>
                  <List spacing="xs" size="sm">
                    {summary.learningObjectives.slice(0, 3).map((objective, index) => (
                      <List.Item key={index}>{objective}</List.Item>
                    ))}
                  </List>
                </Box>
              )}
              
              {summary.coreConcepts.length > 0 && (
                <Box>
                  <Group spacing="xs" mb="xs">
                    <IconBookmark size={20} />
                    <Text weight={600}>Core Concepts</Text>
                  </Group>
                  <List spacing="xs" size="sm">
                    {summary.coreConcepts.slice(0, 4).map((concept, index) => (
                      <List.Item key={index}>{concept}</List.Item>
                    ))}
                  </List>
                </Box>
              )}
              
              {summary.assignmentSummary && (
                <Box>
                  <Group spacing="xs" mb="xs">
                    <IconTool size={20} />
                    <Text weight={600}>Module Project</Text>
                  </Group>
                  <Text size="sm" italic>{summary.assignmentSummary}</Text>
                </Box>
              )}
            </Stack>
          </Paper>
        )}

        <Divider />

        <CourseContentDisplay 
          content={course} 
          onStateChange={handleStateChange}
        />
      </Stack>
    </Paper>
  );
};

export default CourseView; 